import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { RenderUiEditor } from './Editor/RenderUiEditor'
import { RenderUi } from './FormRenderer/RenderUi'
import { TabPreviewForm } from './FormRenderer/TabPreviewForm'
import { PreviewModeContext } from './FormRenderer/RenderUiFromSpec'
import clsx from 'clsx'
import { useUiFromSpecContext } from './EditorContextProvider'
import { UseFormReturn } from 'react-hook-form'
import { useEffect } from 'react'

export type ViewMode = 'full-form' | 'split' | 'full-preview'

export type UiFromSpecProps = Readonly<{
  // setValue: any
  // control: any
  inputSize: any
  // watch: any
  form: UseFormReturn<any>
  onSubmit?: (data: any) => void
  allowpreview?: boolean
  formHeader?: React.ReactNode
  rightPanel?: React.ReactNode
  className?: string
  leftPanelClassname?: string
  wrapperLeftPanelClassname?: string
  componentPanelClassname?: string
  rightPanelClassname?: string
  outerLeftPanelClassname?: string
  innerLeftPanelClassname?: string
  canvasClassname?: string
  renderViewOnly?: boolean
  viewMode?: ViewMode
}>

export function UiFromSpec(props: UiFromSpecProps) {
  const {
    formHeader,
    rightPanel,
    leftPanelClassname,
    canvasClassname,
    outerLeftPanelClassname,
    wrapperLeftPanelClassname,
    componentPanelClassname,
    rightPanelClassname,
    innerLeftPanelClassname,
    className,
    renderViewOnly = false,
    viewMode,
  } = props

  const { designState, setEditor } = useUiFromSpecContext()

  useEffect(() => {
    if (viewMode === 'full-form' || viewMode === 'split') {
      setEditor(true)
    } else if (viewMode === 'full-preview' || renderViewOnly) {
      setEditor(false)
    }
  }, [viewMode, renderViewOnly])

  // Full preview mode — just render the form, no editor chrome
  if (viewMode === 'full-preview' || renderViewOnly) {
    return (
      <DndProvider backend={HTML5Backend}>
        <RenderUi {...props} />
      </DndProvider>
    )
  }

  const canvasStyle = {
    backgroundColor: `${designState.backgroundColor || '#ffffff'}`,
    color: `${designState.textColor || '#000000'}`,
  }

  return (
    <DndProvider backend={HTML5Backend} >
      <div className={clsx('flex flex-row w-full bg-slate-50 p-2', className)}>
        {/* Left panel — component palette + layer panel */}
        <div
          className={clsx(
            !wrapperLeftPanelClassname && 'flex-none w-1/5 max-w-[324px] min-w-[240px]',
            wrapperLeftPanelClassname
          )}
        >
          <RenderUiEditor
            form={props.form}
            leftPanelClassname={leftPanelClassname}
            outerLeftPanelClassname={outerLeftPanelClassname}
            innerLeftPanelClassname={innerLeftPanelClassname}
            componentPanelClassname={componentPanelClassname}
          />
        </div>
        <div className='flex flex-row w-full w-4/5 h-[calc(100vh-141px)] overflow-auto'>

          {/* Canvas — editor */}
          <div className={clsx(
            viewMode === 'split' ? 'flex-1 px-2 min-w-0 overflow-auto' : (!canvasClassname && 'flex-1 px-4'),
            !viewMode && canvasClassname,
            "w-full"

          )}>
            <div className="h-min w-full p-4 rounded-lg shadow-sm border" style={canvasStyle}>
              {/* Hide the old Form Definition / Form Preview tabs when viewMode is managed externally */}
              {formHeader ? formHeader : <TabPreviewForm allowpreview={viewMode ? false : props.allowpreview} />}
              <RenderUi {...props} />
            </div>
          </div>

          {/* Split preview panel */}
          {viewMode === 'split' && (
            <div className="flex-1 px-2 min-w-0 overflow-auto border-l border-slate-200">
              <div className="h-min w-full p-4 rounded-lg shadow-sm border" style={canvasStyle}>
                <div className="text-xs text-slate-400 font-medium mb-2 uppercase tracking-wide">Preview</div>
                <PreviewModeContext.Provider value={true}>
                  <RenderUi {...props} />
                </PreviewModeContext.Provider>
              </div>
            </div>
          )}

          {/* Custom right panel (original behaviour) */}
          {rightPanel && !viewMode && (
            <div
              className={clsx(!rightPanelClassname && 'flex-none w-1/6 min-w-[180px] max-w-[324px]', rightPanelClassname)}
            >
              {rightPanel}
            </div>
          )}

        </div>
      </div>
    </DndProvider>
  )
}
